import { Component, ViewChild, ElementRef } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { UserService } from '../services/user.service';
import { FormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { isPossiblePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';
import { CompanyService } from '../services/company.service';

@Component({
  selector: 'app-account',
  imports: [FormsModule, MatIcon, CommonModule],
  templateUrl: './account.component.html',
  styleUrl: './account.component.css',
})
export class AccountComponent {

  title = 'Account';
    userName: string | null = null;
    company: string | null = null;
    id: string | null = null;
    @ViewChild('fileInput') fileInput!: ElementRef;
    @ViewChild('fileInputLogo') fileInputLogo!: ElementRef;
    user: any = {};
    companyLogo: string | null = null;
    updateSuccess = false;
    error: string | null = null;
    readOnlySso = false;
  

    currentPassword: string = '';
    newPassword: string = '';
    confirmPassword: string = '';
    passwordError: string | null = null;
    showPasswordChangeDialog: boolean = false;
    isDownloading = false;
  
    constructor(
      private authService: AuthService,
      private userService: UserService,
      private companyService: CompanyService
    ) {}

   ngOnInit() {
    const session = this.authService.getSession();
    this.readOnlySso = session?.authSource === 'microsoft'
      && session.accessLevel === 'readOnly';

    if (this.readOnlySso && session) {
      this.user = {
        id: session.user.id,
        username: session.user.userName,
        firstname: session.user.firstName,
        lastname: session.user.lastName,
        email: session.user.email,
        company: session.customer?.name,
        roles: session.user.roles,
        profilePhoto: null
      };
      this.companyLogo = session.customer?.logoUrl ?? null;
      return;
    }

    const userId = this.authService.getUserId();
    if (userId) {
      this.userService.getUserById(userId).subscribe({
        next: (res) => this.user = res,
        error: (err) => console.error(err)
      });
    } else {
      console.error('No user ID found');
    }

    const companyId = this.authService.getCompanyId();
    if (companyId) {
      this.companyService.getCompanyById(companyId).subscribe({
        next: (company) => this.companyLogo = company.companyLogo,
        error: (err) => console.error('Failed to load company logo:', err)
      });
    }
   }

   formatPhone(phone: string | undefined): string {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  }

  onFileSelected(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      const formData = new FormData();
      formData.append('file', file);

      this.userService.uploadProfilePhoto(this.user.id, formData).subscribe({
        next: (res: { url: any; }) => {
          this.user.profilePhoto = res.url;
          this.authService.setProfilePhoto(res.url);
        },
        error: (err: any) => console.error('Upload failed', err)
      });
    }
  }

  onFileSelectedLogo(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      const allowed = ['.jpg', '.jpeg', '.png'];
      const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
      if (!allowed.includes(ext)) {
        this.error = 'Invalid file type. Only JPG and PNG files are allowed.';
        input.value = '';
        return;
      }
      this.error = null;
      const formData = new FormData();
      formData.append('file', file);
      const companyId = this.authService.getCompanyId() || '';

      this.companyService.uploadCompanyLogo(companyId, formData).subscribe({
        next: () => {
          // Refetch company to get the stored logo path (raw blob URL won't work with private storage)
          this.companyService.getCompanyById(companyId).subscribe({
            next: (company) => {
              this.companyLogo = company.companyLogo;
              this.authService.setCompanyLogo(company.companyLogo);
            }
          });
          input.value = '';
        },
        error: (err: any) => {
          console.error('Logo upload failed', err);
          this.error = 'Logo upload failed. Please try again.';
          input.value = '';
        }
      });
    }
  }

  validatePhoneFormat(phone: string | undefined): boolean {
  if (!phone) return true;
  return isValidPhoneNumber(phone, 'CA'); 
}

  saveChanges() {
    this.error = null;

    if (!this.validatePhoneFormat(this.user.phoneNumber)) {
      console.log('Invalid phone number format. Please enter a valid phone number.');
      this.error = 'Invalid phone number format. Please enter a valid phone number.';
      return;
    }

    this.userService.updateUserContact(this.user.id, { firstname: this.user.firstname, lastname: this.user.lastname, email: this.user.email, phoneNumber: this.user.phoneNumber, jobTitle: this.user.jobTitle }).subscribe({
      next: () => {
        this.updateSuccess = true;

        setTimeout(() => {
          const successMessage = document.querySelector('.success-message');
          if (successMessage) {
            successMessage.classList.add('fade-out');

            setTimeout(() => {
              this.updateSuccess = false;
            }, 500);
          }
        }, 2500);
      },
      error: (err) => console.error(err)
    });
  }

  downloadInstaller(): void {
    this.isDownloading = true;
    this.userService.downloadInstaller().subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'DSDN_Tekla_Macro_Installer.msi';
        a.click();
        window.URL.revokeObjectURL(url);
        this.isDownloading = false;
      },
      error: (err) => {
        console.error('Installer download failed', err);
        this.error = 'Failed to download installer. Please try again.';
        this.isDownloading = false;
      }
    });
  }

  openPasswordChangeDialog(): void {
    this.showPasswordChangeDialog = true;
    this.newPassword = '';
    this.confirmPassword = '';
    this.passwordError = null;
  }

  closePasswordChangeDialog(): void {
  this.showPasswordChangeDialog = false;
  this.currentPassword = '';
  this.newPassword = '';
  this.confirmPassword = '';
  this.passwordError = null;
}

updatePassword(): void {
  this.passwordError = null;

  // Basic validation
  if (!this.newPassword || !this.confirmPassword) {
    this.passwordError = 'All fields are required';
    return;
  }
  
  if (this.newPassword !== this.confirmPassword) {
    this.passwordError = 'New passwords do not match';
    return;
  }

  this.userService.updateUserPassword(localStorage.getItem('id'), this.newPassword, this.currentPassword).subscribe({
    next: () => {
      console.log('Password updated successfully');
      this.closePasswordChangeDialog();
      this.updateSuccess = true;
      
      // Hide success message after some time
      setTimeout(() => {
        this.updateSuccess = false;
      }, 3000);
    },
    error: (err) => {
      console.error('Failed to update password', err);
      this.passwordError = 'Failed to update password. Please check your current password and try again.';
    }
  });
}

}
